import { useEffect, useState } from "react";
import {
  Button,
  Card,
  Text,
  Image,
  Group,
  ThemeIcon,
  Stack,
  Flex,
  RingProgress,
  Grid,
  useComputedColorScheme,
  Box,
} from "@mantine/core";
import { createStyles } from "@mantine/emotion";
import { keyframes } from "@emotion/react";
import { IconAlertCircle, IconCheck, IconCpu, IconVersions, IconWifi } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { formatDateAndTime, getVersionStatus } from "../../utils/CommonUtil";
import { getDeviceImg, isSingleControllerDevice } from "../../utils/productUtils";
import { useTranslation } from "react-i18next";
import DeviceStatusBadge from "../new_test_ui/new_test_steps/common/DeviceStatusBadge";
import ControllerBatteryWarningIcon from "../new_test_ui/new_test_steps/common/ControllerBatteryWarningIcon";
import {
  mapControllerConnectionState,
  normalizeControllerBattery,
} from "@utils/controllerBatteryUtils";

const updateGlow = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(255, 145, 43, 0); }
  50% { box-shadow: 0 0 0 6px rgba(255, 145, 43, 0.45); }
`;

const useStyles = createStyles((theme, _, u) => ({
  card: {
    transition: "transform 150ms ease, box-shadow 150ms ease",
    cursor: "pointer",

    "&:hover": {
      transform: "scale(1.01)",
      boxShadow: theme.shadows.md,
    },
  },

  updateSection: {
    animation: `${updateGlow} 1.6s ease-in-out infinite`,
    borderTop: `1px solid ${theme.colors.orange[6]}`,
  },

  icon: {
    [u.dark]: {
      color: theme.colors.dark[0],
    },

    [u.light]: {
      color: theme.colors.gray[0],
    },
  },
}));

export interface DeviceCardProps {
  product: any;
  status: number;
  isSelected?: boolean;
  onSelect: (product: any) => void;
}

export default function DeviceCard(props: DeviceCardProps) {
  const { t } = useTranslation();
  const { classes, theme } = useStyles();
  const navigate = useNavigate();
  const colorScheme = useComputedColorScheme("light");
  const [osUpdateRequired, setOsUpdateRequired] = useState(false);
  const [appUpdateRequired, setAppUpdateRequired] = useState(false);

  useEffect(() => {
    let current = true;
    if (props.product.version) {
      getVersionStatus(props.product.version, props.product.uuid).then(
        ({ osUpdateReq, appUpdateReq }) => {
          if (!current) return;
          setOsUpdateRequired(osUpdateReq);
          setAppUpdateRequired(appUpdateReq);
        },
      );
    }
    return () => {
      current = false;
    };
  }, [props.product.version, props.product.uuid]);

  const updateNeeded = appUpdateRequired || osUpdateRequired;

  return (
    <Card
      className={classes.card}
      maw={"438px"}
      withBorder
      shadow="lg"
      data-testid={`device-card-${props.product.uuid}`}
      style={{
        borderColor: props.isSelected ? theme.colors.blue[6] : undefined,
        borderWidth: props.isSelected ? 2 : undefined,
      }}
      onClick={() => props.onSelect(props.product)}
    >
      <Card.Section withBorder style={{ position: "relative" }}>
        <Image
          p={"md"}
          src={getDeviceImg(props.product.uuid)}
          height={160}
          w={"100%"}
          fit="contain"
          alt="Device"
        />
        <Box pos="absolute" top={12} left={12} style={{ zIndex: 2 }}>
          <ControllerBatteryWarningIcon
            controllerState={mapControllerConnectionState(props.product.controllers)}
            controllerBattery={normalizeControllerBattery(props.product.controllerBattery)}
            singleController={isSingleControllerDevice(props.product.uuid)}
          />
        </Box>
      </Card.Section>
      <Card.Section p={"sm"} withBorder>
        <Flex justify={"space-between"} align={"center"}>
          {props.product.alias ? (
            <Stack gap="0" justify={"flex-start"}>
              <Text fz="lg" fw={600}>
                {props.product.alias.toUpperCase()}
              </Text>
              <Text fz="xs" c="dimmed">
                {props.product.uuid}
              </Text>
            </Stack>
          ) : (
            <Text fz="lg" fw={500}>
              {props.product.uuid}
            </Text>
          )}
          <DeviceStatusBadge deviceStatus={props.status} dontPoll={true} />
        </Flex>
      </Card.Section>
      <Card.Section p={"sm"} withBorder>
        <Grid align="flex-start" justify={"space-between"}>
          <Grid.Col span={8}>
            <Stack gap="xs" align={"flex-start"} justify="flex-start">
              {props.product.version ? (
                <Group gap={"xs"} wrap="nowrap">
                  <ThemeIcon size="sm" color={"blue"}>
                    <IconCpu />
                  </ThemeIcon>
                  <Text c="dimmed" w={300} fz={"sm"}>
                    {t("DeviceCard.appVersion") + " " + props.product.version}
                  </Text>
                </Group>
              ) : null}
              {props.product.puiVersion ? (
                <Group gap={"xs"} wrap="nowrap">
                  <ThemeIcon size="sm" color={"blue"}>
                    <IconVersions />
                  </ThemeIcon>
                  <Text c="dimmed" w={300} fz={"sm"}>
                    {t("DeviceCard.osVersion") + " " + props.product.puiVersion}
                  </Text>
                </Group>
              ) : null}
              {props.product.vrDate ? (
                <Group gap={"xs"} wrap="nowrap">
                  <ThemeIcon size="sm" color={"blue"}>
                    <IconWifi />
                  </ThemeIcon>
                  <Text c="dimmed" w={300} fz={"sm"}>
                    {t("DeviceCard.lastAccess") + " " + formatDateAndTime(props.product.vrDate)}
                  </Text>
                </Group>
              ) : null}
            </Stack>
          </Grid.Col>
          <Grid.Col span={4} p={0}>
            <RingProgress
              sections={[
                {
                  value: Number(props.product.battery) || 0,
                  color:
                    Number(props.product.battery) > 20
                      ? Number(props.product.battery) > 60
                        ? "blue"
                        : "orange"
                      : "red",
                },
              ]}
              rootColor={colorScheme === "dark" ? theme.colors.dark[4] : theme.colors.gray[4]}
              size={100}
              thickness={8}
              label={
                <Stack gap="0">
                  <Text
                    color={
                      Number(props.product.battery) > 20
                        ? Number(props.product.battery) > 60
                          ? "blue"
                          : "orange"
                        : "red"
                    }
                    fw={700}
                    ta="center"
                    size="sm"
                  >
                    {props.product.battery ? props.product.battery + "%" : "N/A"}
                  </Text>
                  <Text ta="center" fz="xs" c="dimmed">
                    Battery
                  </Text>
                </Stack>
              }
            />
          </Grid.Col>
        </Grid>
      </Card.Section>

      {process.env.REACT_APP_OFFLINE !== "true" && (
        <Card.Section
          p={"sm"}
          withBorder
          className={updateNeeded ? classes.updateSection : undefined}
        >
          <Flex justify={"space-between"} align={"center"}>
            {appUpdateRequired || osUpdateRequired ? (
              <Group gap={"xs"} wrap="nowrap">
                <ThemeIcon color="orange" size="sm">
                  <IconAlertCircle />
                </ThemeIcon>
                <Text c="dimmed" fz={"sm"}>
                  {t("DeviceCard.newUpdateAvailable")}
                </Text>
              </Group>
            ) : (
              <Group gap={"xs"} wrap="nowrap">
                <ThemeIcon
                  color="gray"
                  size="sm"
                  variant={colorScheme === "dark" ? "light" : "filled"}
                >
                  <IconCheck />
                </ThemeIcon>
                <Text c="dimmed" fz={"sm"}>
                  {t("VersionControl.noUpdateInfo")}
                </Text>
              </Group>
            )}
            <Button
              onClick={(e) => {
                e.stopPropagation();
                navigate("/version-control?pid=" + props.product.uuid);
              }}
              color={appUpdateRequired || osUpdateRequired ? "orange" : "gray"}
              variant={
                colorScheme === "dark" && !(appUpdateRequired || osUpdateRequired)
                  ? "light"
                  : "filled"
              }
              size="compact-md"
            >
              {appUpdateRequired || osUpdateRequired ? t("DeviceCard.updatePage") : t("DevicePage")}
            </Button>
          </Flex>
        </Card.Section>
      )}
    </Card>
  );
}
